import React, { useEffect, useState, useContext } from "react";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import {
  approveRejectNotification,
  generateReport,
} from "../../service/Notification";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import { Button } from "../../components/common/Button";
import { Download } from "lucide-react";
import CommonTooltip from "../../components/common/CommonTooltip";
import { downloadSkippedAccountsExcel } from "../../utils/skippedAccountsExport";
import { getDefaultRouteFromPermissions } from "../../utils/permissions";

const NotificationSection = () => {
  const [openTab, setOpenTab] = useState(1);
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const {
    langMode,
    notifications,
    fetchNotifications,
    isLoading,
    currentPage,
    setCurrentPage,
    setPageSize,
    setIsLoading,
    updateNotificationStatus,
    updateNotificationReportStatus,
    theme,
    noMoreNotifications,
    setNoMoreNotifications,
  } = context;

  useEffect(() => {
    const initializeNotifications = async () => {
      setCurrentPage(0);
      setPageSize(10);
      setNoMoreNotifications(false);
      setIsLoading(true); // Start loading
      await fetchNotifications(0); // Fetch initial data
      setIsLoading(false); // Stop loading after fetching
    };

    initializeNotifications();
  }, []);

  useEffect(() => {
    if (currentPage > 0) {
      fetchNotifications(currentPage);
    }
  }, [currentPage]);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.offsetHeight;
    if (
      scrollTop + windowHeight >= documentHeight - 100 &&
      !isLoading &&
      !noMoreNotifications
    ) {
      setIsLoading(true);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading, noMoreNotifications]);

  // Removed exact duplicate of the effect above: having both registered the
  // same scroll listener twice, so every scroll-to-bottom event incremented
  // currentPage by 2 instead of 1, silently skipping a page of notifications.
  // useEffect(() => {
  //   window.addEventListener("scroll", handleScroll);
  //   return () => {
  //     window.removeEventListener("scroll", handleScroll);
  //   };
  // }, [isLoading, noMoreNotifications]);

  const handleApproveReject = async (
    referenceId,
    notificationId,
    isApproved
  ) => {
    try {
      const data = await approveRejectNotification(
        referenceId,
        notificationId,
        isApproved
      );
      if (data.header.code === 600) {
        toast.success(data.body.value);
        updateNotificationStatus(notificationId, isApproved);
      }
    } catch (error) {
      console.error("Error while approving/rejecting:", error);
    }
  };

  const handleGenerateReport = async (referenceId, notificationId) => {
    try {
      const data = await generateReport(referenceId, notificationId);
      if (data.header.code === 600) {
        toast.success(data.body.value);
        updateNotificationReportStatus(notificationId);
      } else if (data.header.code === 607) {
        toast.error(data.body.value);
      }
    } catch (error) {
      console.error("Error while generating report:", error);
    }
  };

  const getRandomColor = () => {
    const colors = ["#EF4444", "#F97316", "#F59E0B", "#EAB308", "#84CC16", "#22C55E", "#10B981", "#14B8A6", "#06B6D4",
      "#0EA5E9", "#3B82F6", "#6366F1", "#8B5CF6", "#A855F7", "#D946EF", "#EC4899", "#F43F5E",];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    return randomColor;
  };

  const formatCreatedAtDate = (createdAt) => {
    const date = new Date(createdAt);
    const formattedDate = `${date.getDate()} ${date.toLocaleString("default", {
      month: "long",
    })} ${date.getFullYear()}, ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
    return formattedDate;
  };

  const handleGoBack = () => {
    // The page the user came from is only saved when notifications are opened from the bell.
    // If it is missing (page opened directly or refreshed) or is this page itself, navigate()
    // would do nothing, so go back in history, or to the default landing page.
    const path = Cookies.get("notipreviousPath");
    if (path && path !== "/notification") {
      navigate(path);
    } else if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(getDefaultRouteFromPermissions());
    }
  };

  // Bulk PDF export notifications (BLF-2171) carry the exportId as their
  // referenceId, so they can be deep-linked straight to that row of the
  // export-history screen.
  const isBulkPdfExportNotification = (notification) =>
    String(notification?.notificationType || "")
      .toUpperCase()
      .includes("PDF") && notification?.referenceId != null;
  // "Inventory Export Ready/Failed" notifications also carry the exportId as their
  // referenceId; the inventory screen opens its export history on that export.
  const isInventoryExportNotification = (notification) =>
    notification?.referenceId != null &&
    (String(notification?.notificationType || "").toUpperCase().includes("INVENTORY_EXPORT") ||
      String(notification?.heading || "").toLowerCase().startsWith("inventory export"));
  // The backend appends the export's counts, e.g. "(16474 items, 56 log entries)";
  // those are left out of inventory export notifications.
  const notificationBody = (notification) =>
    isInventoryExportNotification(notification)
      ? String(notification.body ?? "")
          .replace(/\s*\(\s*[\d,]+\s+items?\b[^)]*\)/gi, "")
          .trim()
      : notification.body;
  return (
    <div>
      <div className='flex items-center gap-2 ml-4'>
        <button
          onClick={handleGoBack}
          className="
                       p-1
                       bg-blue-100
                       text-blue-800
                       rounded-full
                       shadow-md
                       hover:bg-blue-200
                       focus:outline-none
                       focus:ring-2
                       focus:ring-blue-400
                       focus:ring-opacity-75
                       transition
                       duration-200
                       ease-in-out
                       flex items-center justify-center
                       min-h-[36px] h-9 w-9
                       "
          aria-label="Go back"
        >
          <HiOutlineArrowLongLeft size={24} />
        </button>
        <p className='uppercase font-semibold'>{translations[langMode].notification}</p>
      </div>
      <div className="w-full">
        <div
          className={`px-5 py-5 bg-white overflow-y-auto ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}>
          <div>
          
            <div
              className="inline-flex gap-1 overflow-hidden rounded-lg bg-gray-100 p-1 shadow-sm dark:bg-gray-800"
              role="group"
            >
              <Button
                isActive={openTab === 1}
                onClick={() => setOpenTab(1)}
              >
                {translations[langMode].allnotification}
              </Button>
              <Button
                isActive={openTab === 2}
                onClick={() => setOpenTab(2)}
              >
                {translations[langMode].request}
              </Button>
            </div>
            <div className="mt-5 space-y-4" style={{ display: openTab === 1 ? "block" : "none" }}>
              {notifications.map((notification, index) => (
                <div
                  key={index}
                  className={`rounded-xl shadow-md border transition-all duration-200 hover:shadow-lg ${theme === "dark"
                    ? "bg-gray-800 border-gray-700"
                    : theme === "high-contrast"
                      ? "bg-black border-white"
                      : "bg-white border-blue-200"
                    } ${notification.isRead === 0 ? "bg-blue-50" : ""}`}
                >
                  <div className="flex justify-between items-start p-5">
                    <div className="w-2/3 space-y-2">
                      <span
                        className="inline-block text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm"
                        style={{ backgroundColor: getRandomColor() }}
                      >
                        {notification.notificationId}
                      </span>

                      <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200">
                        {notification.user}{" "}
                        <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                          {notification.heading}
                        </span>
                      </h3>

                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {notificationBody(notification)}
                      </p>
                    </div>
                    <div className="w-1/3 text-right space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatCreatedAtDate(notification.createdAt)}
                      </p>

                      {notification.notificationType === "REPORT_REQUEST" &&
                        notification.isAcknowledged === 0 && (
                          <button data-button-intent="primary"
                            type="button"
                            onClick={() =>
                              handleGenerateReport(
                                notification.referenceId,
                                notification.notificationId
                              )
                            }
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow focus:ring-2 focus:ring-blue-300 dark:bg-blue-700 dark:hover:bg-blue-800"
                          >
                            {translations[langMode].generatereport}
                          </button>
                        )}

                      {Array.isArray(notification.skippedAccounts) && notification.skippedAccounts.length > 0 && (
                        <CommonTooltip
                          text={translations[langMode]?.protectionJobDownloadSkippedTooltip ?? "Download Excel of skipped accounts"}
                          position="top"
                          disableDefaultDescription
                          tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                          fitContent
                          clampToViewport
                        >
                          <button
                            type="button"
                            onClick={() =>
                              downloadSkippedAccountsExcel(
                                notification.skippedAccounts,
                                `skipped-accounts-notification-${notification.notificationId}`
                              )
                            }
                            aria-label={translations[langMode]?.protectionJobDownloadSkippedTooltip ?? "Download Excel of skipped accounts"}
                            data-testid={`download-skipped-notification-${notification.notificationId}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#1d5a96]"
                          >
                            <Download size={13} />
                            {translations[langMode]?.download ?? "Download"}
                          </button>
                        </CommonTooltip>
                      )}

                      {isBulkPdfExportNotification(notification) && (
                        <button data-button-intent="primary"
                          type="button"
                          onClick={() =>
                            navigate(`/pdf-export-history?exportId=${notification.referenceId}`)
                          }
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow focus:ring-2 focus:ring-blue-300 dark:bg-blue-700 dark:hover:bg-blue-800"
                        >
                          {translations[langMode]?.bulkExportViewExport ?? "View export"}
                        </button>
                      )}

                      {isInventoryExportNotification(notification) && (
                        <button data-button-intent="primary"
                          type="button"
                          onClick={() =>
                            navigate(`/inventory-management?exportId=${notification.referenceId}`)
                          }
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow focus:ring-2 focus:ring-blue-300 dark:bg-blue-700 dark:hover:bg-blue-800"
                        >
                          {translations[langMode]?.inventoryExportView ?? "View export"}
                        </button>
                      )}

                      {notification.notificationType === "EXTENSION" &&
                        notification.isApproved === 0 && (
                          <div className="flex gap-2 justify-end">
                            <button data-button-intent="danger"
                              type="button"
                              onClick={() =>
                                handleApproveReject(
                                  notification.referenceId,
                                  notification.notificationId,
                                  false
                                )
                              }
                              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg border dark:bg-gray-700 dark:text-gray-300"
                            >
                                {translations[langMode].Reject}
                            </button>
                            <button data-button-intent="success"
                              type="button"
                              onClick={() =>
                                handleApproveReject(
                                  notification.referenceId,
                                  notification.notificationId,
                                  true
                                )
                              }
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow focus:ring-2 focus:ring-blue-300 dark:bg-blue-700 dark:hover:bg-blue-800"
                            >
                              {translations[langMode].Approve}
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-4" style={{ display: openTab === 2 ? "block" : "none" }}>
              {notifications
                .filter((notification) => notification.isApproved === 0)
                .map((notification, index) => (
                  <div
                    key={index}
                    className={`rounded-xl shadow-md border transition-all duration-200 hover:shadow-lg p-5 ${theme === "dark"
                        ? "bg-gray-800 border-gray-700"
                        : theme === "high-contrast"
                          ? "bg-black border-white"
                          : "bg-white border-blue-200"
                      }`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      {/* Left Side */}
                      <div className="flex-1 space-y-2">
                        <span
                          className="inline-block text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm"
                          style={{ backgroundColor: getRandomColor() }}
                        >
                          {notification.notificationId}
                        </span>

                        <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200">
                          {notification.user}{" "}
                          <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                            {notification.heading}
                          </span>
                        </h3>

                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          {notificationBody(notification)}
                        </p>
                      </div>

                      {/* Right Side */}
                      <div className="text-right space-y-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatCreatedAtDate(notification.createdAt)}
                        </p>

                        {notification.notificationType === "REPORT_REQUEST" &&
                          notification.isAcknowledged === 0 && (
                            <button data-button-intent="primary"
                              type="button"
                              onClick={() =>
                                handleGenerateReport(
                                  notification.referenceId,
                                  notification.notificationId
                                )
                              }
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow focus:ring-2 focus:ring-blue-300 dark:bg-blue-700 dark:hover:bg-blue-800"
                            >
                              {translations[langMode].generatereport}
                            </button>
                          )}

                        {notification.notificationType === "EXTENSION" &&
                          notification.isApproved === 0 && (
                            <div className="flex gap-2 justify-end">
                              <button data-button-intent="danger"
                                type="button"
                                onClick={() =>
                                  handleApproveReject(
                                    notification.referenceId,
                                    notification.notificationId,
                                    false
                                  )
                                }
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg border dark:bg-gray-700 dark:text-gray-300"
                              >
                                {translations[langMode].Reject}
                              </button>
                              <button data-button-intent="success"
                                type="button"
                                onClick={() =>
                                  handleApproveReject(
                                    notification.referenceId,
                                    notification.notificationId,
                                    true
                                  )
                                }
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow focus:ring-2 focus:ring-blue-300 dark:bg-blue-700 dark:hover:bg-blue-800"
                              >
                                 {translations[langMode].Approve}
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {isLoading && (
              <div className="text-center py-4">
                <p>{translations[langMode].loadingmorenotification}</p>
                <Spinner />
              </div>
            )}
            {!isLoading && noMoreNotifications && (
              <div className="text-center py-4">
                <p>{translations[langMode].nomorenotification}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSection;
